import { useCallback, useState } from 'react';
import { api } from '@/lib/api';

export type SaveTxtState = 'idle' | 'saving' | 'success' | 'error';

/**
 * Fetch the plain-text body of a note and trigger a browser file download.
 *
 * Flow:
 *   idle -> saving -> (success | error) -> idle (after ~2s)
 *
 * Filename format: note-<sanitized-template-name>-<YYYY-MM-DD>.txt
 *   Sanitize: whitespace and /\:*?"<>| replaced with -, consecutive dashes
 *   collapsed, lowercased, leading/trailing dashes trimmed.
 */
export function useSaveAsTxt(): {
  state: SaveTxtState;
  save: (noteId: string, templateName: string, noteDate: string) => Promise<void>;
} {
  const [state, setState] = useState<SaveTxtState>('idle');

  const save = useCallback(async (noteId: string, templateName: string, noteDate: string) => {
    setState('saving');
    try {
      const text = await api.get<string>(`/api/notes/${noteId}/plain-text`);

      // Build filename
      const sanitized = templateName
        .toLowerCase()
        .replace(/[\s/\\:*?"<>|]+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-+|-+$/g, '');
      const dateStr = noteDate.slice(0, 10); // YYYY-MM-DD
      const filename = `note-${sanitized}-${dateStr}.txt`;

      // Trigger download
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(url);

      setState('success');
    } catch {
      setState('error');
    } finally {
      setTimeout(() => setState('idle'), 2_000);
    }
  }, []);

  return { state, save };
}
